/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { injectIntl } from 'react-intl';
import { useParams } from 'react-router';
import { TextField } from '../../components/TextFieldInput';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { variants } from '../../utils/motionVariants';
import { CardHeader } from '../../components/CardHeader';
import { TextArea } from '../../components/TextAreaInput';
import { Button } from '../../components/Button';
import axios from 'axios';
import { UploadFile } from '../../components/UploadFile';
import { v4 } from 'uuid';

const LABEL_SIZE = 120;

function getSchema(intl) {
  return Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[!-ÿ\s]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.NAME.REGEX' })
      ),
    description: Yup.string(),
    externalId: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[a-z0-9-.]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.CODE.REGEX' })
      ),
    floorPlanURI: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
  });
}

function FloorPlansCreateEdit({ intl, history }) {
  let { areaId, facilityId } = useParams();
  const [initialValues] = useState({
    name: '',
    description: '',
    floorPlanURI: '',
    externalId: v4(),
  });
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: getSchema(intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      try {
        const { data } = await axios.post(
          `/areas/${areaId}/facilities/${facilityId}/floor-plans`,
          values
        );
        history.push(
          `/geo-planner/areas/${areaId}/facilities/${facilityId}/floor-plans/${data.id}/crop`
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  async function fileUpload(file) {
    const config = {
      headers: {
        'content-type': 'application/pdf',
      },
    };
    const base64 = await toBase64(file);
    const { data } = await axios.post(
      '/upload-pdfs',
      { file: base64, name: file.name },
      config
    );
    if (data) {
      formik.setFieldValue('floorPlanURI', data.path);
      formik.setFieldTouched('floorPlanURI');
    }
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      className="card"
    >
      <CardHeader
        title={intl.formatMessage({
          id: 'AREAS.FACILITIES.FLOORPLANS.NEW.TITLE',
        })}
        subtitle={intl.formatMessage({
          id: 'AREAS.FACILITIES.FLOORPLANS.NEW.SUBTITLE',
        })}
      />
      <div className="card-body">
        <div className="col-sm-12 col-md-12 col-lg-4 px-0">
          <TextField
            field="externalId"
            inputSize="100%"
            labelMinSize={LABEL_SIZE}
            formik={formik}
            label="ID"
          />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-4 px-0">
          <TextField
            field="name"
            inputSize="100%"
            labelMinSize={LABEL_SIZE}
            formik={formik}
            label={intl.formatMessage({
              id: 'AREAS.FACILITIES.FLOORPLANS.NEW.NAME',
            })}
          />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-4 px-0">
          <TextArea
            direction="align-items-center"
            field="description"
            inputSize="100%"
            labelMinSize={LABEL_SIZE}
            formik={formik}
            label={intl.formatMessage({ id: 'DESCRIPTION' })}
          />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-4 px-0">
          <div style={{ marginLeft: LABEL_SIZE }}>
            <UploadFile
              send={fileUpload}
              afterClear={() => {
                formik.setFieldValue('floorPlanURI', '');
                formik.setFieldTouched('floorPlanURI', false);
              }}
            />
            {formik.errors['floorPlanURI'] ? (
              <div className="d-flex">
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors['floorPlanURI']}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="d-flex mt-6">
          <Button
            classes="w-75px px-12 py-2 my-1 d-flex align-items-center justify-content-center btn btn-primary"
            type="submit"
            formik={formik}
            onClick={formik.handleSubmit}
            title={intl.formatMessage({ id: 'SAVE' })}
          />
          <Button
            classes="w-75px cancel-bg ml-3 cancel-btn cancel-text-color px-4"
            type="button"
            onClick={history.goBack}
            title={intl.formatMessage({ id: 'CANCEL' })}
          />
        </div>
      </div>
    </motion.div>
  );
}

export default injectIntl(FloorPlansCreateEdit);
